import Box from '@amzn/awsui-components-core/box';
import { missingForCreate } from 'src/components/star-grid/utils/studioCreateReadiness';
import { StudioPreviewFilters } from 'src/components/star-grid/components/StudioPreviewFilters';
import React from 'react';
import Alert from '@amzn/awsui-components-core/alert';
import Button from '@amzn/awsui-components-core/button';
import type { ReportStudioState } from 'src/components/star-grid/hooks/useReportStudio';
import type { QueryResponse } from 'src/components/star-grid/api/reportEngineTypes';
import { ReportResultGrid } from 'src/components/star-grid/components/ReportResultGrid';
import { studioPreviewTitle } from 'src/components/star-grid/utils/studioDraft';
import styles from 'src/components/star-grid/components/styles/ReportStudio.module.scss';
interface Props {
  readonly state: ReportStudioState;
  readonly previewGrid?: QueryResponse;
  readonly previewRunning: boolean;
  readonly previewError?: string;
  readonly onSave: () => void | Promise<void>;
  readonly saving: boolean;
  /** Whether the spec names a data source (the P&L cube or a datasource). */
  readonly canSave: boolean;
}
export const StudioPreview: React.FC<Props> = ({ state, previewGrid, previewRunning, previewError, onSave, saving, canSave }) => {
  // A report with no rows or no columns renders nothing, so it is not creatable; a note beside the button says what is missing.
  const missing = missingForCreate({
    name: state.draft.name,
    rows: state.deck.rows.length,
    columns: state.deck.columns.length,
    hasSource: canSave
  });
  const reportName = studioPreviewTitle(state.draft.name, previewGrid?.synthetic ? state.text('studioSampleNumbers') : undefined);
  return (
    <section className={styles.preview} data-testid="studio-layout-preview">
      <header className={styles.panelHeader}>
        <h2>{state.text('studioLivePreview')}</h2>
        {/* Only when the response says so, or real numbers would read as sample data. */}
        {previewGrid?.synthetic && <span className={styles.previewTag}>{state.text('studioSampleNumbers')}</span>}
        <span className={styles.spacer} />
        {missing.length > 0 && (
          <Box variant="small" color="text-status-inactive" data-testid="studio-save-missing">
            {state.text('studioCreateNeeds').replace('{items}', missing.map((item) => state.text(`studioCreateNeeds_${item}`)).join(', '))}
          </Box>
        )}
        <Button data-testid="studio-save" variant="primary" loading={saving} disabled={missing.length > 0} onClick={() => void onSave()}>
          {state.text(state.editingReportId ? 'studioSaveReport' : 'studioCreateReport')}
        </Button>
      </header>
      <StudioPreviewFilters state={state} />
      <div className={styles.previewBody}>
        {/* The server's detail names the axis or column that failed. */}
        {previewError && <Alert type="error">{previewError}</Alert>}
        {/* A cold Create page has no cube to query until a template or import supplies one. The grid
            stays mounted so the layout does not jump when the first response lands. */}
        {!previewGrid && !previewRunning && (
          <p className={styles.help}>
            {state.text(!missing.includes('rows') && !missing.includes('columns') ? 'studioPreviewNeedsTemplate' : 'studioPreviewHelp')}
          </p>
        )}
        {/* Not compact: the preview has to show the deck's two-row scenario-group header as the saved report does. */}
        <ReportResultGrid
          autoHeight
          response={previewGrid}
          text={state.text}
          loading={previewRunning}
          scaleId={state.scaleId}
          reportName={reportName}
        />
      </div>
    </section>
  );
};
